import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ThemeOptions } from '../../theme';
import { InternalUserDetails, LogToDetails, UserDetails } from '../../types/user';

export interface UserState extends InternalUserDetails {
	currentTheme: ThemeOptions | null; // legacy, to be removed in future
}

const initialState: UserState = {
	id: '',
	currentTheme: 'default',
	name: 'Unknown Name',
	email: 'unknown@email.com',
	orgs: [
		{
			name: '<No Organisations>',
			id: '_',
			roles: [],
			permissions: ['assign:operation', 'delete:operation', 'assign:role'],
		},
	],
	roles: ['TEST_ROLE_ONLY', 'DEFAULT-OPERATION'], // this specifically contains characters not allowed by Azure to prevent accidental usage in deployment
};

const userSlice = createSlice({
	name: 'user',
	initialState,
	reducers: {
		loginUser: (state, action: PayloadAction<LogToDetails>) => {
			return {
				...state,
				id: action.payload.id || initialState.id,
				name: action.payload.name || initialState.name,
				email: action.payload.email || initialState.email,
				orgs: action.payload.orgs || initialState.orgs,
				roles: action.payload.roles || initialState.roles,
			};
		},
		addNewRole: (state, action: PayloadAction<string>) => {
			return {
				...state,
				roles: [...state.roles, action.payload],
			};
		},
		removeRole: (state, action: PayloadAction<string>) => {
			const newRoles = state.roles.filter((role) => role.toLocaleLowerCase() !== action.payload.toLocaleLowerCase());
			return {
				...state,
				roles: newRoles,
			};
		},
	},
});

export const getUserDetails = createSelector([(state) => state.user], (user: UserState) => {
	return {
		name: user.name,
		email: user.email,
		orgs: user.orgs,
		roles: user.roles,
	} as UserDetails;
});

export const getUserOrgsString = createSelector([(state) => state.user], (user: UserState) => {
	return user.orgs.map((org) => org.name).join(', ');
});

export const hasViewAllPermission = createSelector([(state) => state.user], (user: UserState) => {
	return user.orgs.some((org) => org.permissions.includes('view_all:operation'));
});

export const { loginUser, addNewRole, removeRole } = userSlice.actions;
export default userSlice.reducer;
